import { useState, useMemo, Suspense, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useTexture } from '@react-three/drei';
import * as THREE from 'three';

const BrickWall = ({ 
  wallWidth, 
  wallHeight, 
  wallThickness, 
  doors,
  doorWidth,
  doorHeight,
  doorPosition // posición en metros desde el borde izquierdo
}) => {
  // Crear forma de la pared con huecos usando useMemo
  const geometry = useMemo(() => {
    const shape = new THREE.Shape()
      .moveTo(-wallWidth/2, -wallHeight/2)
      .lineTo(wallWidth/2, -wallHeight/2)
      .lineTo(wallWidth/2, wallHeight/2)
      .lineTo(-wallWidth/2, wallHeight/2)
      .lineTo(-wallWidth/2, -wallHeight/2);

    // Añadir puertas
    for (let i = 0; i < doors; i++) {
      // Calcular posición horizontal
      const xPos = doors === 1 
        ? -wallWidth/2 + doorPosition
        : -wallWidth/2 + (i + 1) * (wallWidth / (doors + 1));
      
      const doorPath = new THREE.Path()
        .moveTo(xPos - doorWidth/2, -wallHeight/2)
        .lineTo(xPos + doorWidth/2, -wallHeight/2)
        .lineTo(xPos + doorWidth/2, -wallHeight/2 + doorHeight)
        .lineTo(xPos - doorWidth/2, -wallHeight/2 + doorHeight)
        .lineTo(xPos - doorWidth/2, -wallHeight/2);
      
      shape.holes.push(doorPath);
    }

    // Configuración de extrusión
    const extrudeSettings = {
      steps: 1,
      depth: wallThickness,
      bevelEnabled: false
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geometry.computeVertexNormals();
    geometry.center();

    return geometry;
  }, [wallWidth, wallHeight, wallThickness, doors, doorWidth, doorHeight, doorPosition]);

  // Texturas
  const brickTexture = useTexture({
    map: 'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/brick_diffuse.jpg',
    normalMap: 'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/brick_bump.jpg',
    roughnessMap: 'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/brick_roughness.jpg'
  });

  // Configurar repetición de textura
  Object.keys(brickTexture).forEach(key => {
    brickTexture[key].wrapS = brickTexture[key].wrapT = THREE.RepeatWrapping;
    brickTexture[key].repeat.set(wallWidth/2, wallHeight/2);
  });

  return (
    <mesh geometry={geometry} rotation={[0, Math.PI, 0]}>
      <meshStandardMaterial
        {...brickTexture}
        side={THREE.DoubleSide}
        roughness={0.8}
        metalness={0.2}
      />
    </mesh>
  );
};

export default function DoorEditor() {
  const [wallWidth, setWallWidth] = useState(10);
  const [wallHeight, setWallHeight] = useState(6);
  const [wallThickness, setWallThickness] = useState(0.5);
  
  // Configuración de puertas
  const [doorCount, setDoorCount] = useState(1);
  const [doorWidth, setDoorWidth] = useState(2);
  const [doorHeight, setDoorHeight] = useState(3);
  const [doorPosition, setDoorPosition] = useState(5); // en metros desde el borde izquierdo
  const [positionError, setPositionError] = useState('');

  // Validar que la puerta quede dentro de la pared
  useEffect(() => {
    if (doorCount === 1) {
      const minPosition = doorWidth/2;
      const maxPosition = wallWidth - doorWidth/2;
      
      if (doorPosition < minPosition || doorPosition > maxPosition) {
        setPositionError(`Posición debe estar entre ${minPosition.toFixed(2)}m y ${maxPosition.toFixed(2)}m`);
      } else {
        setPositionError('');
      }
    } else {
      setPositionError('');
    }
  }, [doorPosition, doorWidth, wallWidth, doorCount]);

  // Ajustar posición si el ancho de la pared cambia
  useEffect(() => {
    if (doorCount === 1) {
      const maxPosition = wallWidth - doorWidth/2;
      if (doorPosition > maxPosition) {
        setDoorPosition(maxPosition);
      }
    }
  }, [wallWidth, doorWidth, doorCount]);

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <header className="bg-gray-800 text-white p-4">
        <h1 className="text-2xl font-bold">Editor de Puertas en Pared</h1>
        <p className="text-sm">Posición en metros desde el borde izquierdo</p>
      </header>

      <div className="flex flex-1">
        {/* Panel de control */}
        <div className="w-64 bg-gray-200 p-4 space-y-4">
          <h2 className="text-lg font-semibold">Pared</h2>
          
          <div className="space-y-2">
            <label className="block">
              Ancho: {wallWidth}m
              <input
                type="range"
                min="5"
                max="20"
                step="0.5"
                value={wallWidth}
                onChange={(e) => setWallWidth(parseFloat(e.target.value))}
                className="w-full"
              />
            </label>

            <label className="block">
              Alto: {wallHeight}m
              <input
                type="range"
                min="3"
                max="10"
                step="0.5"
                value={wallHeight}
                onChange={(e) => setWallHeight(parseFloat(e.target.value))}
                className="w-full"
              />
            </label>

            <label className="block">
              Grosor: {wallThickness}m
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.05"
                value={wallThickness}
                onChange={(e) => setWallThickness(parseFloat(e.target.value))}
                className="w-full"
              />
            </label>
          </div>

          <h2 className="text-lg font-semibold">Puertas</h2>
          
          <div className="space-y-2">
            <label className="block">
              Cantidad:
              <input
                type="number"
                min="1"
                max="5"
                value={doorCount}
                onChange={(e) => setDoorCount(Math.min(5, Math.max(1, parseInt(e.target.value) || 1)))}
                className="w-full p-1 border rounded"
              />
            </label>

            {doorCount === 1 && (
              <>
                <label className="block">
                  Posición desde izquierda: {doorPosition.toFixed(2)}m
                  <input
                    type="range"
                    min={doorWidth/2}
                    max={wallWidth - doorWidth/2}
                    step="0.1"
                    value={doorPosition}
                    onChange={(e) => setDoorPosition(parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-xs text-gray-600">
                    Rango válido: {doorWidth/2}m a {(wallWidth - doorWidth/2).toFixed(2)}m
                  </div>
                </label>
                {positionError && (
                  <div className="text-red-500 text-sm">{positionError}</div>
                )}
              </>
            )}

            <label className="block">
              Ancho: {doorWidth}m
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={doorWidth}
                onChange={(e) => {
                  const newWidth = parseFloat(e.target.value);
                  setDoorWidth(newWidth);
                  // Ajustar posición si es necesario
                  if (doorCount === 1 && doorPosition > wallWidth - newWidth/2) {
                    setDoorPosition(wallWidth - newWidth/2);
                  }
                }}
                className="w-full"
              />
            </label>

            <label className="block">
              Alto: {doorHeight}m
              <input
                type="range"
                min="1"
                max="4"
                step="0.1"
                value={doorHeight}
                onChange={(e) => setDoorHeight(parseFloat(e.target.value))}
                className="w-full"
              />
            </label>
          </div>
        </div>

        {/* Vista 3D */}
        <div className="flex-1">
          <Canvas camera={{ position: [0, 0, 15], fov: 50 }}>
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} intensity={1} />
            <Suspense fallback={null}>
              <BrickWall 
                wallWidth={wallWidth}
                wallHeight={wallHeight}
                wallThickness={wallThickness}
                doors={doorCount}
                doorWidth={doorWidth}
                doorHeight={doorHeight}
                doorPosition={doorPosition}
              />
            </Suspense>
            <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
          </Canvas>
        </div>
      </div>
    </div>
  );
}